export const createJianyingRequestGuard = () => {
  let currentRequestId = 0;
  return {
    begin: () => {
      currentRequestId += 1;
      return currentRequestId;
    },
    invalidate: () => {
      currentRequestId += 1;
    },
    isCurrent: (requestId) => requestId === currentRequestId,
  };
};

const hasOwn = (value, key) => (
  Boolean(value) && Object.prototype.hasOwnProperty.call(value, key)
);

const segmenter = typeof Intl.Segmenter === 'function'
  ? new Intl.Segmenter('zh-CN', { granularity: 'grapheme' })
  : null;

const getImageUrl = (image = {}) => (
  image.image_url || image.content || image.oss_url || image.url || ''
);

export const normalizeJianyingOrderImages = (images = []) => (
  (Array.isArray(images) ? images : [])
    .map((image, index) => {
      const originalUrl = getImageUrl(image);
      return {
        id: image.id || image.image_id || `image-${index}`,
        name: image.name || image.file_name || image.original_name || `图片 ${index + 1}`,
        url: originalUrl,
        originalUrl,
        smartCropUrl: image.smart_crop_image_url || image.smartCropUrl || '',
        smartCropStatus: image.smart_crop_status || image.smartCropStatus || 'pending',
        smartCropError: image.smart_crop_error || image.smartCropError || '',
      };
    })
    .filter((image) => image.url)
);

export const createJianyingImageValue = (image, source) => ({
  kind: 'image',
  image_id: image.id,
  source: ['original', 'smart_crop'].includes(source)
    ? source
    : image.smartCropStatus === 'succeeded' && image.smartCropUrl
      ? 'smart_crop'
      : 'original',
});

export const applyJianyingSmartCropResults = (values = {}, results = []) => {
  const successfulIds = new Set(
    (Array.isArray(results) ? results : [])
      .filter((item) => item?.status === 'succeeded' && item.smart_crop_image_url)
      .map((item) => String(item.image_id)),
  );
  if (!successfulIds.size) return values;

  let changed = false;
  const nextValues = Object.fromEntries(Object.entries(values).map(([slotId, value]) => {
    if (
      !value?.image_id
      || value.source === 'manual'
      || !successfulIds.has(String(value.image_id))
      || value.source === 'smart_crop'
    ) {
      return [slotId, value];
    }
    changed = true;
    return [slotId, { ...value, source: 'smart_crop' }];
  }));
  return changed ? nextValues : values;
};

const compareByOrder = (left, right) => (
  Number(left?.sort_order || 0) - Number(right?.sort_order || 0)
  || String(left?.id || '').localeCompare(String(right?.id || ''))
);

const compareSlotsByTimeline = (left, right) => {
  const leftStartUs = Number(
    left?.slot_settings?.timeline_start_us
    ?? left?.slot_settings?.target_start_us
  );
  const rightStartUs = Number(
    right?.slot_settings?.timeline_start_us
    ?? right?.slot_settings?.target_start_us
  );
  const hasLeftStart = Number.isFinite(leftStartUs);
  const hasRightStart = Number.isFinite(rightStartUs);

  if (hasLeftStart && hasRightStart && leftStartUs !== rightStartUs) {
    return leftStartUs - rightStartUs;
  }
  if (hasLeftStart !== hasRightStart) return hasLeftStart ? -1 : 1;
  return compareByOrder(left, right);
};

const toProductionSlot = (slot) => ({
  ...slot,
  key: slot.id,
  description: slot.name,
  runtime: slot.slot_settings || {},
  config: {
    material_id: slot.material_id,
    segment_id: slot.segment_id,
    occurrence_id: slot.occurrence_id,
    ...(slot.slot_settings || {}),
  },
});

const getTemplateSlots = (template, includeDisabled = false) => (
  (Array.isArray(template?.slots) ? template.slots : [])
    .filter((slot) => slot?.id && (includeDisabled || slot.replace_enabled !== false))
    .sort(compareSlotsByTimeline)
    .map(toProductionSlot)
);

export const buildJianyingSlotSections = (
  template,
  kind = null,
  { includeDisabled = false, includeEmptyGroups = false } = {},
) => {
  const slots = getTemplateSlots(template, includeDisabled)
    .filter((slot) => !kind || slot.kind === kind);
  const groups = (Array.isArray(template?.slot_groups) ? template.slot_groups : [])
    .slice()
    .sort(compareByOrder);
  const nodesByGroupId = new Map(groups.map((group) => [
    group.id,
    { type: 'group', key: group.id, group, children: [] },
  ]));
  const rootNodes = [];

  for (const group of groups) {
    const node = nodesByGroupId.get(group.id);
    const parent = group.parent_group_id
      ? nodesByGroupId.get(group.parent_group_id)
      : null;
    (parent?.children || rootNodes).push(node);
  }
  for (const slot of slots) {
    const node = { type: 'slot', key: slot.id, slot };
    const parent = slot.group_id ? nodesByGroupId.get(slot.group_id) : null;
    (parent?.children || rootNodes).push(node);
  }

  const pruneEmptyGroups = (nodes) => nodes.flatMap((node) => {
    if (node.type === 'slot') return [node];
    const children = pruneEmptyGroups(node.children);
    return children.length || includeEmptyGroups ? [{ ...node, children }] : [];
  });
  return pruneEmptyGroups(rootNodes);
};

export const buildJianyingTimelineSlotEntries = (
  template,
  { includeDisabled = false } = {},
) => {
  const groupsById = new Map(
    (Array.isArray(template?.slot_groups) ? template.slot_groups : [])
      .map((group) => [group.id, group]),
  );
  const getGroupPath = (groupId) => {
    const names = [];
    let group = groupId ? groupsById.get(groupId) : null;
    while (group) {
      names.unshift(group.name);
      group = group.parent_group_id
        ? groupsById.get(group.parent_group_id)
        : null;
    }
    return names;
  };

  return getTemplateSlots(template, includeDisabled).map((slot) => ({
    type: 'slot',
    key: slot.id,
    slot,
    groupPath: getGroupPath(slot.group_id),
  }));
};

const getSlotsByKind = (template, kind) => (
  getTemplateSlots(template).filter((slot) => slot.kind === kind)
);

export const getJianyingTextSlots = (template) => getSlotsByKind(template, 'text');
export const getJianyingImageSlots = (template) => getSlotsByKind(template, 'image');
export const getJianyingAudioSlots = (template) => getSlotsByKind(template, 'audio');
export const getJianyingVideoSlots = (template) => getSlotsByKind(template, 'video');

export const getJianyingDefaultCopyPresetId = (template) => (
  template?.default_copy_preset_id
  || template?.copy_presets?.find((preset) => preset?.is_default)?.id
  || ''
);

const resolveCopyText = (value, clientName) => (
  String(value ?? '').replace('{{客户姓名}}', String(clientName ?? ''))
);

export const applyJianyingCopyPreset = (
  template,
  currentValues = {},
  presetId,
  clientName = '',
) => {
  const preset = (Array.isArray(template?.copy_presets) ? template.copy_presets : [])
    .find((item) => String(item.id) === String(presetId));
  if (!preset) throw new Error('找不到所选文案模板');

  const nextValues = { ...currentValues };
  for (const slot of getJianyingTextSlots(template)) {
    if (!hasOwn(preset.values, slot.id)) {
      throw new Error(`文案模板未覆盖文字槽位：${slot.name}`);
    }
    nextValues[slot.id] = {
      kind: 'text',
      text: resolveCopyText(preset.values[slot.id], clientName),
    };
  }
  return nextValues;
};

export const createInitialJianyingSlotValues = (
  template,
  clientName = '',
  images = [],
) => {
  const textSlots = getJianyingTextSlots(template);
  const presetId = getJianyingDefaultCopyPresetId(template);
  let values = {};
  if (textSlots.length) {
    if (!presetId) throw new Error('模板没有默认文案模板');
    values = applyJianyingCopyPreset(template, values, presetId, clientName);
  }

  const orderImages = normalizeJianyingOrderImages(images);
  if (orderImages.length) {
    getJianyingImageSlots(template).forEach((slot, index) => {
      values[slot.id] = createJianyingImageValue(
        orderImages[index % orderImages.length],
      );
    });
  }

  return values;
};

const getText = (value) => String(value?.text ?? value ?? '');
const hasImage = (value) => Boolean(
  value?.image_id || value?.media_url || value?.image_url || value?.url,
);
const hasAudio = (value) => Boolean(value?.file_name && Number(value?.duration_us) > 0);
const hasVideo = (value) => Boolean(
  value?.file_name
  && Number(value?.file_duration_us) > 0
  && Number(value?.source_duration_us) > 0,
);

export const getJianyingTextMetrics = (value) => {
  const text = String(value ?? '').replace(/\r\n?/g, '\n');
  const visibleText = text.replace(/\n/g, '');
  return {
    characterCount: segmenter
      ? [...segmenter.segment(visibleText)].length
      : Array.from(visibleText).length,
    lineCount: text === '' ? 0 : text.split('\n').length,
  };
};

export const isJianyingTextMetricOverLimit = (slot, metrics) => {
  const maxLength = Number(slot?.runtime?.max_length);
  const maxLines = Number(slot?.runtime?.max_lines);
  return {
    characters: Number.isInteger(maxLength) && maxLength > 0
      && metrics.characterCount > maxLength,
    lines: Number.isInteger(maxLines) && maxLines > 0
      && metrics.lineCount > maxLines,
  };
};

export const isCompactJianyingTextSlot = (slot) => {
  const maxLength = Number(slot?.runtime?.max_length);
  return slot?.runtime?.input_mode === 'single_line'
    && Number.isInteger(maxLength)
    && maxLength > 0
    && maxLength <= 30;
};

export const getJianyingTextFieldErrors = (template, values = {}) => {
  const fieldErrors = {};
  for (const slot of getJianyingTextSlots(template)) {
    const text = getText(values[slot.id]);
    const metrics = getJianyingTextMetrics(text);
    const errors = [];
    if (slot.required && text === '') errors.push(`${slot.name}为必填项`);
    if (slot.runtime?.input_mode === 'single_line' && /[\r\n]/.test(text)) {
      errors.push('单行文字不能包含换行');
    }
    if (slot.runtime?.max_length && metrics.characterCount > slot.runtime.max_length) {
      errors.push(`最多输入 ${slot.runtime.max_length} 个字，当前已输入 ${metrics.characterCount} 个字`);
    }
    if (slot.runtime?.max_lines && metrics.lineCount > slot.runtime.max_lines) {
      errors.push(`最多输入 ${slot.runtime.max_lines} 行，当前为 ${metrics.lineCount} 行`);
    }
    if (errors.length) fieldErrors[slot.id] = errors;
  }
  return fieldErrors;
};

export const normalizeJianyingFieldErrors = (fieldErrors) => {
  if (!fieldErrors || typeof fieldErrors !== 'object' || Array.isArray(fieldErrors)) {
    return {};
  }
  return Object.fromEntries(Object.entries(fieldErrors).flatMap(([key, errors]) => {
    const normalized = (Array.isArray(errors) ? errors : [errors])
      .filter((error) => typeof error === 'string' && error.trim());
    return normalized.length ? [[key, normalized]] : [];
  }));
};

export const getFirstJianyingFieldErrorKey = (template, fieldErrors = {}) => (
  getJianyingTextSlots(template)
    .find((slot) => Array.isArray(fieldErrors[slot.id]) && fieldErrors[slot.id].length)
    ?.id || ''
);

export const getFirstJianyingImageFieldErrorKey = (
  template,
  imageFieldErrors = {},
) => (
  getJianyingImageSlots(template)
    .find((slot) => Boolean(imageFieldErrors[slot.id]))
    ?.id || ''
);

export const validateJianyingSlotValues = (template, values = {}) => {
  const pageErrors = [];
  const audioFieldErrors = {};
  const imageFieldErrors = {};
  const videoFieldErrors = {};

  for (const slot of getTemplateSlots(template)) {
    if (slot.kind === 'text') continue;
    const value = values[slot.id];
    if (slot.kind === 'audio') {
      if (slot.required && !hasAudio(value)) {
        const error = `${slot.name} 不能为空`;
        pageErrors.push(error);
        audioFieldErrors[slot.id] = error;
        continue;
      }
      const maxDurationUs = Number(slot.slot_settings?.duration_us);
      if (hasAudio(value) && maxDurationUs > 0 && Number(value.duration_us) > maxDurationUs + 50_000) {
        const error = `${slot.name} 的音频时长超过模板上限`;
        pageErrors.push(error);
        audioFieldErrors[slot.id] = error;
      }
      continue;
    }
    if (slot.kind === 'video') {
      if (slot.required && !hasVideo(value)) {
        const error = `${slot.name} 不能为空`;
        pageErrors.push(error);
        videoFieldErrors[slot.id] = error;
        continue;
      }
      const fileDurationUs = Number(value?.file_duration_us);
      const sourceStartUs = Number(value?.source_start_us);
      const sourceDurationUs = Number(value?.source_duration_us);
      if (hasVideo(value) && (
        !Number.isSafeInteger(sourceStartUs)
        || sourceStartUs < 0
        || !Number.isSafeInteger(sourceDurationUs)
        || sourceDurationUs <= 0
        || sourceStartUs + sourceDurationUs > fileDurationUs
      )) {
        const error = `${slot.name} 的视频选择范围无效`;
        pageErrors.push(error);
        videoFieldErrors[slot.id] = error;
      }
      continue;
    }
    if (slot.required && !hasImage(value)) {
      const error = `${slot.name} 不能为空`;
      pageErrors.push(error);
      imageFieldErrors[slot.id] = error;
    }
  }
  return {
    audioFieldErrors,
    imageFieldErrors,
    videoFieldErrors,
    fieldErrors: getJianyingTextFieldErrors(template, values),
    pageErrors,
  };
};
